import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertCircle, Loader2, ShieldAlert, Video, FileText, Image as ImageIcon,
  UploadCloud, X, Check,
} from "lucide-react";
import { userApi, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

const PROOF_SLOTS = [
  {
    kind: "payment_video",
    label: "Video of you making the payment",
    hint: "Record your UPI/banking app screen while paying — not a screen recording of our app. Max 60 MB.",
    accept: "video/mp4,video/quicktime,video/webm,video/x-matroska,video/3gpp,video/mpeg",
    Icon: Video,
  },
  {
    kind: "bank_statement",
    label: "Bank statement showing the debit",
    hint: "PDF export from your bank or a screenshot of the statement line. Max 60 MB.",
    accept: "image/png,image/jpeg,image/webp,application/pdf",
    Icon: FileText,
  },
  {
    kind: "payment_screenshot",
    label: "Payment success screenshot",
    hint: "The success screen from UPI / your bank with the UTR visible. Max 60 MB.",
    accept: "image/png,image/jpeg,image/webp",
    Icon: ImageIcon,
  },
];

function ProofSlot({ slot, value, onChange, onClear, disabled }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pick = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) {
      setErr("File too large (max 60 MB)");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", slot.kind);
      const r = await userApi.post("/uploads/dispute-proof", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({
        ...r.data,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        isVideo: file.type.startsWith("video/"),
      });
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const Icon = slot.Icon;

  return (
    <div data-testid={`dispute-proof-slot-${slot.kind}`}>
      <input
        ref={ref}
        type="file"
        accept={slot.accept}
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
        data-testid={`dispute-proof-input-${slot.kind}`}
      />
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <Label className="text-sm font-medium">{slot.label}</Label>
          <p className="mt-0.5 text-xs text-muted-foreground">{slot.hint}</p>
          {!value ? (
            <button
              type="button"
              onClick={() => ref.current?.click()}
              disabled={disabled || busy}
              className="mt-2 flex w-full items-center gap-2 rounded-lg border border-dashed bg-card px-3 py-2 text-left text-xs transition-colors hover:border-primary hover:bg-secondary disabled:opacity-50"
              data-testid={`dispute-proof-pick-${slot.kind}`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <UploadCloud className="h-4 w-4 text-primary" />}
              <span className="text-foreground">{busy ? "Uploading…" : "Choose file"}</span>
              <span className="ml-auto text-muted-foreground">{(slot.accept.match(/\/[a-z0-9+-]+/g) || []).join(" · ").replace(/\//g, "").toUpperCase()}</span>
            </button>
          ) : (
            <div className="mt-2 flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
              {value.preview ? (
                <img src={value.preview} alt="" className="h-10 w-10 shrink-0 rounded-md border object-cover" />
              ) : value.isVideo ? (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-900 text-white">
                  <Video className="h-4 w-4" />
                </span>
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium" data-testid={`dispute-proof-name-${slot.kind}`}>
                  {value.filename}
                </p>
                <p className="inline-flex items-center gap-1 text-emerald-700">
                  <Check className="h-3 w-3" /> Uploaded ({Math.round((value.size || 0) / 1024)} KB)
                </p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={onClear}
                aria-label="Remove file"
                data-testid={`dispute-proof-remove-${slot.kind}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {err && <p className="mt-1 text-xs text-rose-700" data-testid={`dispute-proof-error-${slot.kind}`}>{err}</p>}
        </div>
      </div>
    </div>
  );
}

export default function DisputeDeposit({ deposit, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [phone, setPhone] = useState(deposit?.contact_phone || "");
  const [proofs, setProofs] = useState({}); // keyed by slot kind -> { file_id, filename, size, preview, isVideo }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!deposit) return null;
  const already = deposit.dispute && deposit.dispute.status === "open";
  const disallowed = deposit.status === "Rejected";

  const resetState = () => {
    setReason("");
    setPhone(deposit?.contact_phone || "");
    setProofs({});
    setErr("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (reason.trim().length < 10) {
      setErr("Please describe the issue in at least 10 characters.");
      return;
    }
    const proofList = Object.entries(proofs).map(([kind, p]) => ({ file_id: p.file_id, kind }));
    if (proofList.length === 0) {
      setErr("Please share at least one proof file (video / bank statement / screenshot).");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await userApi.post(`/deposits/${deposit.id}/dispute`, {
        reason: reason.trim(),
        contact_phone: phone.trim() || null,
        proof_files: proofList,
      });
      toast.success("Dispute opened", {
        description: `We've received your proof — our team will reach out within 2 hours.`,
      });
      setOpen(false);
      resetState();
      qc.invalidateQueries({ queryKey: ["deposits"] });
      qc.invalidateQueries({ queryKey: ["deposit", deposit.id] });
      onDone?.();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  if (already) {
    return (
      <div
        className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        data-testid="dispute-open-banner"
      >
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <p className="font-semibold">Dispute is being reviewed</p>
          <p className="text-xs">
            Our team has your proof{deposit.dispute?.proof_files?.length ? ` (${deposit.dispute.proof_files.length} file${deposit.dispute.proof_files.length > 1 ? "s" : ""})` : ""} and will contact you shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={disallowed}
        className="w-full justify-center gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
        data-testid="dispute-open-button"
      >
        <ShieldAlert className="h-4 w-4" /> Not credited? Raise a dispute
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetState(); }}>
        <AlertDialogContent
          data-testid="dispute-dialog"
          className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Raise a dispute for {deposit.id}</AlertDialogTitle>
            <AlertDialogDescription>
              Share proof of your payment so we can locate and credit it. Upload any combination of a payment video (not a screen recording of our app), a bank statement, and a success screenshot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {err && (
              <Alert variant="destructive" data-testid="dispute-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{err}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label>What happened? (10+ characters)</Label>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                placeholder="e.g. UPI payment is debited from my bank at 12:30 but the deposit still shows pending."
                data-testid="dispute-reason-input"
                maxLength={1000}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone we can reach you on (optional)</Label>
              <Input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11"
                data-testid="dispute-phone-input"
                placeholder="9876543210"
              />
            </div>
            <div className="space-y-3 rounded-xl border bg-amber-50/50 p-3">
              <p className="text-sm font-semibold text-amber-900">Attach proof of payment</p>
              {PROOF_SLOTS.map((slot) => (
                <ProofSlot
                  key={slot.kind}
                  slot={slot}
                  value={proofs[slot.kind]}
                  onChange={(val) => setProofs((p) => ({ ...p, [slot.kind]: val }))}
                  onClear={() => setProofs((p) => {
                    const nx = { ...p };
                    delete nx[slot.kind];
                    return nx;
                  })}
                  disabled={busy}
                />
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="dispute-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={submit}
                disabled={busy}
                data-testid="dispute-submit"
                className={cn("bg-amber-700 hover:bg-amber-800")}
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit dispute
              </AlertDialogAction>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
